<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'next/button/Button.vue';
import { BaseTable, BaseTableRow, BaseTableCell } from 'next/table';
import { messageStamp } from 'shared/helpers/timeHelper';
import DeleteCustomReportDialog from './delete/DeleteCustomReportDialog.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const deleteDialogRef = ref(null);

const reports = useMapGetter('wgptCustomReports/getCustomReports');
const uiFlags = useMapGetter('wgptCustomReports/getUIFlags');

const tableHeaders = computed(() => [
  t('WGPT_CUSTOM_REPORTS.LIST.TABLE_HEADER.NAME'),
  t('WGPT_CUSTOM_REPORTS.LIST.TABLE_HEADER.EVENT'),
  t('WGPT_CUSTOM_REPORTS.LIST.UPDATED_ON'),
]);

onMounted(() => {
  store.dispatch('wgptCustomReports/get');
});

function navigateToEdit(report) {
  router.push(
    accountScopedRoute('wgpt_reports_settings_edit', {
      reportId: report.id,
    })
  );
}

const readableDate = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy hh:mm a');
</script>

<template>
  <div>
    <woot-loading-state v-if="uiFlags.isFetching" />

    <p
      v-else-if="!reports.length"
      class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
    >
      {{ t('WGPT_CUSTOM_REPORTS.EMPTY_STATE.TITLE') }}
    </p>

    <BaseTable v-else :headers="tableHeaders" :items="reports">
      <template #row="{ items }">
        <BaseTableRow v-for="report in items" :key="report.id" :item="report">
          <template #default>
            <BaseTableCell class="max-w-0 w-full">
              <div class="flex flex-col items-start gap-1">
                <span class="block text-heading-3 text-n-slate-12">
                  {{ report.name }}
                </span>
                <p
                  v-if="report.description"
                  class="mb-0 text-n-slate-11 text-body-main truncate max-w-xs"
                >
                  {{ report.description }}
                </p>
              </div>
            </BaseTableCell>

            <BaseTableCell>
              <span class="text-body-main text-n-slate-11 whitespace-nowrap">
                {{ report.event_definition_name }}
              </span>
            </BaseTableCell>

            <BaseTableCell :title="readableDateWithTime(report.updated_at)">
              <span class="text-body-main text-n-slate-12 whitespace-nowrap">
                {{ readableDate(report.updated_at) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="end">
              <div class="flex gap-3 justify-end flex-shrink-0">
                <Button
                  v-tooltip.top="$t('WGPT_CUSTOM_REPORTS.LIST.TOOLTIP.EDIT')"
                  icon="i-woot-edit-pen"
                  sm
                  slate
                  @click.stop="navigateToEdit(report)"
                />
                <Button
                  v-tooltip.top="$t('WGPT_CUSTOM_REPORTS.LIST.TOOLTIP.DELETE')"
                  icon="i-woot-bin"
                  sm
                  slate
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  @click.stop="deleteDialogRef.open(report)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <DeleteCustomReportDialog ref="deleteDialogRef" />
  </div>
</template>
